import { IconButton } from '@ori-ui/components';
import { Document, Clear } from '@ori-ui/icons';
import { Tooltip, Avatar, Dialog, DialogContent, DialogTitle, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { MouseEvent, FC, ReactElement } from 'react';
import { useState, useEffect, useCallback } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import type { IThumb, IDetailImage } from '../../interfaces';
import { TranslationKey } from '../../models';
import { useDocumentThumb, useDocumentThumbDetail, useRegistrationData } from '../../models/api';
import { Loading } from '../Loading';

const imagePreviewThumbSize = 80;
const imagePreviewDetailSize = 500;

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  minWidth: '60px',
  minHeight: '40px',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: '6px',
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: 0,
  background: 'transparent',
  color: theme.palette.text.primary,
  textDecoration: 'none',
  cursor: 'pointer',
}));

const DocumentIcon = styled(Document)(({ theme }) => ({
  fontSize: '38px',
}));

interface IThumbWithDetailProps {
  fileName: string;
  documentId: number;
}

export const ThumbWithDetail: FC<IThumbWithDetailProps> = (props) => {
  const { fileName, documentId } = props;

  const [detailOpened, setDetailOpened] = useState(false);
  const [imageThumb, setImageThumb] = useState<IThumb>({});
  const [imageDetail, setImageDetail] = useState<IDetailImage>({});

  const { data: registrationData } = useRegistrationData();

  const customerId = registrationData?.customerId ?? null;

  const documentThumbUrl = useDocumentThumb(customerId, documentId, {
    height: imagePreviewThumbSize,
    width: imagePreviewThumbSize,
  });

  const documentThumbDetailUrl = useDocumentThumbDetail(customerId, documentId, {
    height: imagePreviewDetailSize,
    width: imagePreviewDetailSize,
  });

  const { getTestIdProps } = useTestId();

  const mountOrUpdate = useCallback(() => {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy we need this conditon
      imageThumb &&
      !imageThumb[documentId] &&
      /\.(jpg|jpeg|png|bmp)$/gi.test(fileName) &&
      documentId > 0 &&
      customerId
    ) {
      const currentImages = {};
      setImageThumb({ ...currentImages, [documentId]: documentThumbUrl });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed other property
  }, [customerId, documentId, fileName, imageThumb]);

  useEffect(() => {
    mountOrUpdate();
  }, [customerId, mountOrUpdate]);

  const getDetailImage = (fileNameProperty: string) => {
    const imgUrl = imageDetail[documentId];
    if (imgUrl && detailOpened) {
      return (
        <img
          src={imgUrl}
          alt={fileNameProperty}
        />
      );
    }

    return <Loading />;
  };

  const handleOpenDetail = (event: MouseEvent) => {
    event.preventDefault();
    setDetailOpened(true);

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not always truthy we need this conditon
    if (imageDetail && !imageDetail[documentId] && customerId) {
      const currentImages = {};
      setImageDetail({ ...currentImages, [documentId]: documentThumbDetailUrl });
      // await fetchImageDetail(customerId, documentId);
    }
  };

  const handleDetailClose = () => {
    setDetailOpened(false);
  };

  const getAvatar = (): ReactElement => {
    if (/\.(jpg|jpeg|png|bmp)$/gi.test(fileName) && documentId > 0) {
      const thumb = imageThumb[documentId];

      return (
        <Tooltip
          title={
            <Localization
              id={TranslationKey.showImage}
              defaultMessage={defaultTranslations.showImage}
            />
          }
          onClick={handleOpenDetail}
          {...getTestIdProps('thum-with-detail-tooltip-open-detail')}
        >
          <StyledAvatar src={thumb ?? ''} />
        </Tooltip>
      );
    }

    return <DocumentIcon />;
  };

  const avatar = getAvatar();
  const detailImage = getDetailImage(fileName);

  return (
    <>
      {avatar}
      <Dialog open={detailOpened}>
        <DialogTitle>
          <CloseButton
            onClick={() => handleDetailClose()}
            variant="text"
            color="default"
            {...getTestIdProps('thum-with-detail-bt-close-detail')}
          >
            <Clear />
          </CloseButton>
        </DialogTitle>
        <StyledDialogContent>{detailImage}</StyledDialogContent>
      </Dialog>
    </>
  );
};
